import { Calendar } from 'lucide-react';
import type { Event } from '@casetrace/shared';
import { Skeleton } from '../../../components/ui/skeleton';
import type { TimelineRow } from '../use-case-timeline';
import type { ToggleHighlightHandler } from '../../../hooks/use-toggle-highlight';
import { TimelineEventCard } from './timeline-event-card';

function dayOfYear(dateKey: string): number {
  const d = new Date(dateKey);
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

function daysInYear(dateKey: string): number {
  const year = new Date(dateKey).getFullYear();
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365;
}

function DateHeader({ label, dateKey }: { label: string; dateKey: string }) {
  const pct = ((dayOfYear(dateKey) - 1) / (daysInYear(dateKey) - 1)) * 100;

  return (
    <div className="sticky top-0 z-10 flex items-center gap-3 px-4 pb-3 pt-8">
      <div className="flex shrink-0 items-center gap-2">
        <Calendar size={13} className="text-text-secondary" />
        <span className="text-sm font-bold capitalize text-text-primary">{label}</span>
      </div>
      <div
        className="relative h-[2px] flex-1 rounded-full"
        style={{ background: 'rgba(255,255,255,0.18)' }}
      >
        <div
          className="absolute top-1/2 h-[8px] w-[8px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ left: `${pct}%`, background: 'rgba(255,255,255,0.7)' }}
        />
      </div>
    </div>
  );
}

function LoadMoreRow() {
  return (
    <div className="px-4 py-1">
      <Skeleton className="h-10 w-full rounded-lg" />
    </div>
  );
}

export function RowContent({
  row,
  selectedId,
  expandedEvent,
  highlightedEventIds,
  canToggleHighlight,
  onSelect,
  onDeselect,
  onToggleHighlight,
}: {
  row: TimelineRow;
  selectedId: string | null;
  expandedEvent: Event | null;
  highlightedEventIds: ReadonlySet<string>;
  canToggleHighlight: boolean;
  onSelect: (event: Event) => void;
  onDeselect: () => void;
  onToggleHighlight: ToggleHighlightHandler;
}) {
  if (row.kind === 'header') return <DateHeader label={row.label} dateKey={row.dateKey} />;
  if (row.kind === 'load-more') return <LoadMoreRow />;
  const isSelected = row.event.id === selectedId;
  return (
    <div className="px-4">
      <TimelineEventCard
        event={row.event}
        isSelected={isSelected}
        isHighlighted={highlightedEventIds.has(row.event.id)}
        canToggleHighlight={canToggleHighlight}
        position={row.position}
        expandedEvent={isSelected ? (expandedEvent ?? undefined) : undefined}
        onSelect={onSelect}
        onDeselect={onDeselect}
        onToggleHighlight={onToggleHighlight}
      />
    </div>
  );
}
